import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Создание / редактирование учебного года.
 *
 *   mode='create'                         → POST /api/years_study
 *   mode='edit' year={...}                → PUT  /api/years_study/{id}
 *
 * Формат поля trimesterDates — массив дат YYYY-MM-DD (обычно 0..3 элемента).
 * onSaved(year) вызывается после успеха.
 */
const EMPTY = {
  name: '',
  dateStart: '',
  dateEnd: '',
  dateEndKindergarten: '',
  trimesterDates: [],
  archive: false,
};

export default function YearEditModal({ mode, year, onClose, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mode === 'edit' && year) {
      setData({
        name:                year.name || '',
        dateStart:           year.dateStart || '',
        dateEnd:             year.dateEnd || '',
        dateEndKindergarten: year.dateEndKindergarten || '',
        trimesterDates:      Array.isArray(year.trimesterDates) ? year.trimesterDates : [],
        archive:             !!year.archive,
      });
    } else {
      setData(EMPTY);
    }
  }, [mode, year?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const setTrim = (i, v) => setData((d) => {
    const next = d.trimesterDates.slice();
    next[i] = v;
    return { ...d, trimesterDates: next };
  });
  const addTrim = () => setData((d) => ({ ...d, trimesterDates: [...d.trimesterDates, ''] }));
  const removeTrim = (i) => setData((d) => ({ ...d, trimesterDates: d.trimesterDates.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        name: data.name.trim(),
        dateStart: data.dateStart,
        dateEnd: data.dateEnd,
        dateEndKindergarten: data.dateEndKindergarten || null,
        trimesterDates: data.trimesterDates.filter(Boolean),
        archive: data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/years_study', payload)
        : await api.put(`/api/years_study/${year.id}`, payload);
      onSaved?.(res.year);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новый учебный год' : 'Редактирование года'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Название *</label>
              <input
                className="sd-edit__input"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="например, 2025/2026"
                required
                autoFocus
              />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Начало *</label>
              <input
                className="sd-edit__input"
                type="date"
                value={data.dateStart}
                onChange={(e) => set('dateStart', e.target.value)}
                required
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Окончание *</label>
              <input
                className="sd-edit__input"
                type="date"
                value={data.dateEnd}
                onChange={(e) => set('dateEnd', e.target.value)}
                required
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Окончание сада</label>
              <input
                className="sd-edit__input"
                type="date"
                value={data.dateEndKindergarten}
                onChange={(e) => set('dateEndKindergarten', e.target.value)}
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Триместры</label>
              <div className="trim-list">
                {data.trimesterDates.map((d, i) => (
                  <div key={i} className="trim-row">
                    <span className="trim-row__num">{i + 1}.</span>
                    <input
                      className="sd-edit__input"
                      type="date"
                      value={d}
                      onChange={(e) => setTrim(i, e.target.value)}
                    />
                    <button type="button" className="trim-row__remove" onClick={() => removeTrim(i)} title="Удалить">
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}
                <button type="button" className="trim-add" onClick={addTrim}>
                  <Icon name="plus" size={11} /> Добавить триместр
                </button>
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="ref-checkbox">
                <input
                  type="checkbox"
                  checked={data.archive}
                  onChange={(e) => set('archive', e.target.checked)}
                />
                <span>В архиве (скрыт из выпадашек выбора)</span>
              </label>
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
